import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FiCreditCard, FiPlus } from "react-icons/fi";
import { useEffect, useMemo, useState } from "react";
import { loadStripeFor } from "../../../lib/stripeClient";
import { Card } from "../../ui/Card";
import { cardLabel } from "../../ui/CardLabel";
import { ApiAuth, ApiError } from "../../../lib/api";
import { usePaymentConfig, usePaymentMethods } from "../Billing/hooks";
import { PaymentMethod } from "../Billing/types";
import { useCredit, useInvalidateCredit } from "./hooks";
import { CardConfirmation, useAddCredit } from "./useAddCredit";

type Props = {
  auth: ApiAuth;
  locationId: string | null;
  organizationId: string | null;
};

function formatCents(cents: number) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

const AddCreditDrawer = ({
  isOpen,
  onClose,
  auth,
  locationId,
  organizationId,
  cards,
  stripePublicKey,
  stripeAccount = null,
  onAdded,
  onBalanceMayHaveChanged,
}: {
  isOpen: boolean;
  onClose: () => void;
  auth: ApiAuth;
  locationId: string | null;
  organizationId: string | null;
  cards: PaymentMethod[];
  stripePublicKey: string | null;
  stripeAccount?: string | null;
  onAdded: (addedCents: number) => void;
  onBalanceMayHaveChanged: () => void;
}) => {
  const [amount, setAmount] = useState("");
  const [cardId, setCardId] = useState("");

  // 3-D Secure, when the card asks for it.
  const confirmCard = async (clientSecret: string): Promise<CardConfirmation> => {
    if (!stripePublicKey) return { succeeded: false, message: "Payments are not configured for this clinic." };
    const stripe = await loadStripeFor({ publishableKey: stripePublicKey, stripeAccount });
    if (!stripe) return { succeeded: false, message: "Stripe failed to load." };
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
    if (error) return { succeeded: false, message: error.message || "Card confirmation failed." };
    return { succeeded: paymentIntent?.status === "succeeded", paymentIntentId: paymentIntent?.id };
  };

  const { submit, submitting, errMsg, charged, clearError } = useAddCredit({
    auth,
    locationId,
    organizationId,
    confirmCard,
    onAdded,
    onBalanceMayHaveChanged,
  });

  // Reset + default the card on open. A charge that went through and is not
  // yet recorded is kept: the next "Add" records it, and charges nothing.
  useEffect(() => {
    if (!isOpen) return;
    setAmount("");
    clearError();
    const def = cards.find((c) => c.isDefault) || cards[0];
    setCardId(def?.id || "");
  }, [isOpen, cards, clearError]);

  const typedCents = Math.round(parseFloat(amount || "0") * 100);
  const amountCents = charged ? charged.amountCents : typedCents;
  const amountValid = Number.isFinite(amountCents) && amountCents > 0;
  const canSubmit = !submitting && (Boolean(charged) || (amountValid && Boolean(cardId)));

  const onSubmit = () => {
    if (!canSubmit) return;
    void submit({ amountCents, paymentMethodId: cardId });
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="md" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="lg" fontWeight="600" color="#1a1f36">
          Add credit
        </DrawerHeader>
        <DrawerBody>
          <Stack spacing={4}>
            <Box>
              <Text fontSize="13px" fontWeight="500" color="#1a1f36" mb={1}>
                Amount (CAD)
              </Text>
              <Input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="0.00"
                value={charged ? (charged.amountCents / 100).toFixed(2) : amount}
                onChange={(e) => setAmount(e.target.value)}
                isDisabled={Boolean(charged)}
              />
              {charged ? (
                <Text fontSize="12px" color="#6b7280" mt={1}>
                  Your card was charged {formatCents(charged.amountCents)}. Add it to your
                  balance — you won't be charged again.
                </Text>
              ) : amountValid && (
                <Text fontSize="12px" color="#6b7280" mt={1}>
                  {formatCents(amountCents)} will be added to your balance.
                </Text>
              )}
            </Box>

            <Box>
              <Text fontSize="13px" fontWeight="500" color="#1a1f36" mb={1}>
                Card
              </Text>
              {cards.length === 0 ? (
                <Box
                  px={4}
                  py={3}
                  borderWidth="1px"
                  borderColor="#f0f0f0"
                  borderStyle="dashed"
                  borderRadius="8px"
                  fontSize="13px"
                  color="#6b7280"
                >
                  You don't have a saved card. Add one under{" "}
                  <Text as="span" fontWeight="600">
                    Payment methods
                  </Text>{" "}
                  first.
                </Box>
              ) : (
                <Select
                  value={cardId}
                  onChange={(e) => setCardId(e.target.value)}
                  isDisabled={Boolean(charged)}
                >
                  {cards.map((pm) => (
                    <option key={pm.id} value={pm.id}>
                      {cardLabel(pm)}
                      {pm.isDefault ? " (default)" : ""}
                    </option>
                  ))}
                </Select>
              )}
            </Box>

            {errMsg && (
              <Text fontSize="13px" color="#b42318">
                {errMsg}
              </Text>
            )}

            <HStack justify="flex-end" spacing={2} pt={2}>
              <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
                Cancel
              </Button>
              <Button
                bg="#1a1f36"
                color="white"
                _hover={{ bg: "#2d3348" }}
                onClick={onSubmit}
                isDisabled={!canSubmit}
                isLoading={submitting}
                loadingText="Charging"
              >
                {amountValid ? `Add ${formatCents(amountCents)}` : "Add credit"}
              </Button>
            </HStack>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const CreditsView = ({ auth, locationId, organizationId }: Props) => {
  const credit = useCredit(auth, { organizationId, locationId });
  const config = usePaymentConfig(auth, locationId);
  const methods = usePaymentMethods(auth);
  const invalidateCredit = useInvalidateCredit();
  const toast = useToast();
  const addDisclosure = useDisclosure();

  const cards = useMemo(() => methods.data?.methods ?? [], [methods.data]);
  const available = credit.data?.availableCents ?? 0;
  const reserved = credit.data?.reservedCents ?? 0;
  const positive = available > 0;

  const onAdded = (addedCents: number) => {
    addDisclosure.onClose();
    invalidateCredit();
    toast({
      title: `${formatCents(addedCents)} credit added`,
      status: "success",
      duration: 4000,
      isClosable: true,
    });
  };

  return (
    <Box>
      <Card p={{ base: 5, md: 6 }}>
        <Flex justify="space-between" align="flex-start" mb={4} gap={3}>
          <HStack align="flex-start" spacing={3}>
            <Box
              w="40px"
              h="40px"
              borderRadius="10px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg={positive ? "#dcfae6" : "#f2f4f7"}
              color={positive ? "#067647" : "#6b7280"}
              flexShrink={0}
            >
              <FiCreditCard size={20} />
            </Box>
            <Box>
              <Text fontSize="16px" fontWeight="600" color="#1a1f36">
                Credit
              </Text>
              <Text fontSize="13px" color="#6b7280" mt={1}>
                Account balance you can apply to future visits.
              </Text>
            </Box>
          </HStack>
          <Button
            leftIcon={<FiPlus />}
            bg="#1a1f36"
            color="white"
            _hover={{ bg: "#2d3348" }}
            size="sm"
            borderRadius="8px"
            onClick={addDisclosure.onOpen}
          >
            Add credit
          </Button>
        </Flex>

        {credit.isLoading ? (
          <HStack py={4}>
            <Spinner size="sm" />
            <Text fontSize="13px" color="#6b7280">
              Loading your balance…
            </Text>
          </HStack>
        ) : credit.error ? (
          <Text fontSize="13px" color="#b42318">
            {(credit.error as ApiError)?.message || "Could not load your credit."}
          </Text>
        ) : (
          <Box>
            <HStack align="baseline" spacing={2}>
              <Text
                fontSize="32px"
                fontWeight="700"
                color={positive ? "#067647" : "#1a1f36"}
                lineHeight="1"
              >
                {formatCents(available)}
              </Text>
              <Text fontSize="13px" color="#6b7280">
                available
              </Text>
              {reserved > 0 && (
                <Badge
                  bg="#fef0c7"
                  color="#b54708"
                  borderRadius="full"
                  px={2}
                  fontSize="11px"
                >
                  {formatCents(reserved)} reserved
                </Badge>
              )}
            </HStack>
          </Box>
        )}
      </Card>

      <AddCreditDrawer
        isOpen={addDisclosure.isOpen}
        onClose={addDisclosure.onClose}
        auth={auth}
        locationId={locationId}
        organizationId={organizationId}
        cards={cards}
        stripePublicKey={config.data?.stripePublicKey || null}
        stripeAccount={config.data?.stripeAccount || null}
        onAdded={onAdded}
        onBalanceMayHaveChanged={invalidateCredit}
      />
    </Box>
  );
};
